// src/utils/listHandler.ts
// 新 LISTS API（rl-api-8-lists, api.readlinker.com/lists）クライアント。
//
// 契約サマリ（backend/rl-api-8-lists/handler.py 参照）:
//   GET  /lists                                 → 全リスト一覧（item_count 付き）
//   GET  /lists?list_id=<uuid>                  → 単一リスト詳細
//   GET  /lists?list_id=<uuid>&items=true       → リスト内アイテム一覧（書誌情報マージ済み）
//   POST /lists  operation_type=create_list|update_list|delete_list|reorder_lists
//                |add_item|add_items|update_item|remove_item|reorder_items
//
// レスポンスは axios 経由で `{success, data, message?}` 形式。本ハンドラは
// data 部分をそのまま返す。エラーは AxiosError として呼び出し側に伝播する
// （例: add_item 重複 = 409）。

import axios from 'axios';
import { NEW_API } from '../config/api';
import type {
  UserList, ListItem, ListOpenLevel,
} from '../types/rlTypes';

const ENDPOINT = NEW_API.LISTS;

function authHeaders(idToken: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
}

// ----- GET -----

/** GET /lists — 全リスト一覧（item_count 付き）。自分の一覧なので常にログイン必須 */
export async function fetchLists(idToken: string): Promise<{ items: UserList[] }> {
  const res = await axios.get(ENDPOINT, { headers: authHeaders(idToken) });
  return res.data?.data ?? { items: [] };
}

/** GET /lists?public_user_sub=X — 対象ユーザーの公開リスト一覧（item_count 付き）。認証不要 */
export async function fetchPublicLists(userSub: string): Promise<{ items: UserList[] }> {
  const res = await axios.get(ENDPOINT, { params: { public_user_sub: userSub } });
  return res.data?.data ?? { items: [] };
}

/**
 * GET /lists?list_id=X — 単一リスト詳細。
 * idTokenが無い（未ログイン）場合でも、公開(open_level=public)リストなら
 * バックエンド側が返してくれるため、認証ヘッダー無しでリクエストする
 */
export async function fetchListDetail(listId: string, idToken?: string | null): Promise<UserList> {
  const res = await axios.get(ENDPOINT, {
    headers: idToken ? authHeaders(idToken) : undefined,
    params: { list_id: listId },
  });
  return res.data?.data as UserList;
}

/** GET /lists?list_id=X&items=true — リスト内アイテム一覧（書誌情報マージ済み）。
 * fetchListDetailと同様、未ログインでも公開リストなら取得できる */
export async function fetchListItems(listId: string, idToken?: string | null): Promise<{ items: ListItem[] }> {
  const res = await axios.get(ENDPOINT, {
    headers: idToken ? authHeaders(idToken) : undefined,
    params: { list_id: listId, items: 'true' },
  });
  return res.data?.data ?? { items: [] };
}

/**
 * GET /lists?membership_for=X — 指定rl_book_idが自分のどのリストに入っているかを返す。
 * 書誌詳細ページの「リストに追加」メニューで既に入っているリストにチェックを出すために使う。
 * リスト・アイテム数によらず軽量なので、ページを開くたびに呼び直す想定（永続キャッシュ不要）
 */
export async function fetchListMembership(rlBookId: number, idToken: string): Promise<Set<string>> {
  const res = await axios.get(ENDPOINT, {
    headers: authHeaders(idToken),
    params: { membership_for: rlBookId },
  });
  const listIds: string[] = res.data?.data?.list_ids ?? [];
  return new Set(listIds);
}

// ----- POST （リスト操作） -----

/** create_list */
export async function createList(
  listName: string,
  description: string | null,
  openLevel: ListOpenLevel,
  idToken: string,
): Promise<UserList> {
  const body: Record<string, unknown> = { operation_type: 'create_list', list_name: listName, open_level: openLevel };
  if (description !== null && description !== undefined) body.description = description;
  const res = await axios.post(ENDPOINT, body, { headers: authHeaders(idToken) });
  return res.data?.data as UserList;
}

/** update_list — 部分更新（list_name / description / open_level） */
export async function updateList(
  listId: string,
  updates: { listName?: string; description?: string | null; openLevel?: ListOpenLevel },
  idToken: string,
): Promise<UserList> {
  const body: Record<string, unknown> = { operation_type: 'update_list', list_id: listId };
  if (updates.listName !== undefined) body.list_name = updates.listName;
  if (updates.description !== undefined) body.description = updates.description;
  if (updates.openLevel !== undefined) body.open_level = updates.openLevel;
  const res = await axios.post(ENDPOINT, body, { headers: authHeaders(idToken) });
  return res.data?.data as UserList;
}

/** delete_list */
export async function deleteList(
  listId: string,
  idToken: string,
): Promise<{ deleted_list_id: string; deleted_item_count: number }> {
  const res = await axios.post(
    ENDPOINT,
    { operation_type: 'delete_list', list_id: listId },
    { headers: authHeaders(idToken) },
  );
  return res.data?.data;
}

/** reorder_lists */
export async function reorderLists(
  orderedIds: string[],
  idToken: string,
): Promise<{ reordered_count: number }> {
  const res = await axios.post(
    ENDPOINT,
    { operation_type: 'reorder_lists', ordered_ids: orderedIds },
    { headers: authHeaders(idToken) },
  );
  return res.data?.data;
}

// ----- POST （アイテム操作） -----

/** add_item — 重複時は 409（AxiosError として呼び出し側に伝播） */
export async function addItem(
  listId: string,
  rlBookId: number,
  note: string | null,
  idToken: string,
): Promise<ListItem> {
  const body: Record<string, unknown> = { operation_type: 'add_item', list_id: listId, rl_book_id: rlBookId };
  if (note !== null && note !== undefined) body.note = note;
  const res = await axios.post(ENDPOINT, body, { headers: authHeaders(idToken) });
  return res.data?.data as ListItem;
}

/** update_item — note のみ更新可 */
export async function updateItem(
  listId: string,
  rlBookId: number,
  note: string | null,
  idToken: string,
): Promise<ListItem> {
  const res = await axios.post(
    ENDPOINT,
    { operation_type: 'update_item', list_id: listId, rl_book_id: rlBookId, note },
    { headers: authHeaders(idToken) },
  );
  return res.data?.data as ListItem;
}

/** remove_item */
export async function removeItem(
  listId: string,
  rlBookId: number,
  idToken: string,
): Promise<{ removed_list_id: string; removed_book_id: number }> {
  const res = await axios.post(
    ENDPOINT,
    { operation_type: 'remove_item', list_id: listId, rl_book_id: rlBookId },
    { headers: authHeaders(idToken) },
  );
  return res.data?.data;
}

/** reorder_items */
export async function reorderItems(
  listId: string,
  orderedBookIds: number[],
  idToken: string,
): Promise<{ reordered_count: number }> {
  const res = await axios.post(
    ENDPOINT,
    { operation_type: 'reorder_items', list_id: listId, ordered_book_ids: orderedBookIds },
    { headers: authHeaders(idToken) },
  );
  return res.data?.data;
}

/**
 * 本を別リストへ移動（backend に直接の move 操作は無いため add → remove の2段）。
 * 移動先に既に存在（409）した場合は remove だけ行う。
 */
export async function moveItem(
  sourceListId: string,
  rlBookId: number,
  targetListId: string,
  idToken: string,
): Promise<{ moved: boolean; duplicated: boolean }> {
  let duplicated = false;
  try {
    await addItem(targetListId, rlBookId, null, idToken);
  } catch (err: any) {
    if (err?.response?.status === 409) {
      duplicated = true;
    } else {
      throw err;
    }
  }
  await removeItem(sourceListId, rlBookId, idToken);
  return { moved: true, duplicated };
}
